// Tile explainer (Design card 12) — a circled "i" button that opens a small click-popover with the
// tile's definition + the one caveat that stops it being misread. ⚠ Click, not hover: it must
// stopPropagation (KPI cards are themselves filter buttons, so the explainer never triggers a filter),
// be one-open-at-a-time, and close on a second click / another icon / outside click / Esc. Shipped as a
// <button aria-label aria-expanded> with the popover as role="dialog" so it is keyboard-reachable.
// ⚠ The `text` must state the metric's REAL computation (the honesty floor extends to help text).
import { Info } from 'lucide-react';
import { useEffect, useId, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';

const OPEN_EVENT = 'infotooltip:open'; // one-open-at-a-time coordination without a provider

export function InfoTooltip({ text, className }: { text: string; className?: string }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const onOther = (e: Event) => { if ((e as CustomEvent).detail !== id) setOpen(false); }; // another explainer opened
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onOutside = (e: globalThis.MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    window.addEventListener(OPEN_EVENT, onOther);
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onOutside);
    return () => {
      window.removeEventListener(OPEN_EVENT, onOther);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onOutside);
    };
  }, [open, id]);

  const toggle = (e: ReactMouseEvent) => {
    e.stopPropagation(); // ⚠ never trigger the parent KPI/card filter
    e.preventDefault();
    const next = !open;
    setOpen(next);
    if (next) window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: id }));
  };

  return (
    <span ref={ref} className={`relative inline-flex align-middle ${className ?? ''}`}>
      <button
        type="button"
        aria-label="About this tile"
        aria-expanded={open}
        onClick={toggle}
        className="inline-flex items-center justify-center h-[15px] w-[15px] rounded-full border border-border-subtle bg-bg-surface-2 text-text-faint hover:border-accent-cyan hover:text-accent-cyan cursor-help transition-colors"
      >
        <Info className="h-2.5 w-2.5" />
      </button>
      {open && (
        <span
          role="dialog"
          className="absolute top-[22px] left-0 z-50 w-64 rounded-md border border-border-subtle bg-bg-surface px-2.5 py-2 text-[11px] font-normal normal-case tracking-normal leading-relaxed text-text-secondary shadow-lg text-left"
        >
          {text}
        </span>
      )}
    </span>
  );
}
